"use client";

import React, { useMemo } from "react";
import { Check, Car, DollarSign, Clock, ShieldCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inputClasses = "flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200";
const textareaClasses = "flex min-h-[80px] w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200";
export default function TradeInValuation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const benefits = [{
    icon: <DollarSign className="w-5 h-5 text-primary" />,
    title: "Competitive Market Value",
    description: "We use real-time market data to give you the best possible price."
  }, {
    icon: <Clock className="w-5 h-5 text-primary" />,
    title: "Instant Preliminary Quote",
    description: "Get an estimated valuation range within minutes of submission."
  }, {
    icon: <ShieldCheck className="w-5 h-5 text-primary" />,
    title: "Certified Inspection",
    description: "Our technical team ensures a fair assessment of your vehicle's condition."
  }];
  const years = useMemo(() => {
    const currentYear = 2026;
    const startYear = 1990;
    const yearArray = [];
    for (let i = currentYear; i >= startYear; i--) {
      yearArray.push(i.toString());
    }
    return yearArray;
  }, []);
  return <section id="trade-in-valuation" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {}
          <div ref={ref} className={`space-y-8 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                <Car className="w-4 h-4" />
                <span>Trade-In Program</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white">
                Upgrade Your Ride with <span className="text-primary">Fair Value</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                Ready for something new? Trade in your current vehicle and use its value towards your next purchase. 
                We offer transparent, hassle-free valuations backed by our Pre-Owned Excellence guarantee.
              </p>
            </div>

            <div className="grid gap-6">
              {benefits.map((benefit, index) => <div key={index} data-index={index} className="flex gap-4 p-4 rounded-xl bg-card/30 border border-white/5 hover:bg-card/50 transition-colors">
                  <div className="shrink-0 mt-1 bg-primary/10 p-2 rounded-lg h-fit">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary/20 to-blue-900/20 border border-primary/20">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Check className="w-5 h-5 text-primary" />
                Why trade with Ledotravel?
              </h4>
              <p className="text-sm text-muted-foreground">
                We handle all the paperwork, offer competitive rates, and can apply your trade-in value directly as a down payment on any vehicle in our fleet.
              </p>
            </div>
          </div>

          {}
          <div className={`transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="bg-card border border-border rounded-2xl shadow-2xl p-6 md:p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-white">Request Valuation</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Fill out the details below to receive a preliminary estimate.
                </p>
              </div>

              <FormProvider formId="trade_in_valuation_form_pre_owned_excellence" sectionName="trade-in-valuation" defaultValues={{
              name: "",
              email: "",
              phone: "",
              vehicle_make_model: "",
              vehicle_year: "",
              mileage: "",
              additional_details: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              vehicle_make_model: {
                required: true
              },
              vehicle_year: {
                required: true
              },
              mileage: {
                required: true,
                min: 0
              }
            }} submitText="Get My Valuation" successMessage="Valuation request received! We will contact you shortly with an estimate." errorMessage="Something went wrong. Please try again.">
                <TradeInForm years={years} inputClasses={inputClasses} textareaClasses={textareaClasses} />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function TradeInForm({
  years,
  inputClasses,
  textareaClasses
}: {
  years: string[];
  inputClasses: string;
  textareaClasses: string;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mb-2">
          <Check className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-bold text-white">Request Sent!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
          Submit Another Vehicle
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Your Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="John Doe" className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Phone Number</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="+1 555-123-4567" className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Email Address</FormLabel>
            <FormControl>
              <input {...field} type="email" placeholder="john@example.com" className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="vehicle_make_model" rules={{
        required: "Make & Model is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Vehicle Make & Model</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="e.g. Toyota Camry" className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="vehicle_year" rules={{
        required: "Year is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Vehicle Year</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="h-12 bg-background/50 border-input">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {years.map((year, i) => <SelectItem key={year} value={year} data-index={i}>
                      {year}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="mileage" rules={{
      required: "Mileage is required",
      min: {
        value: 0,
        message: "Must be positive"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Current Mileage (km)</FormLabel>
            <FormControl>
              <input {...field} type="number" placeholder="45000" className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Additional Details (Optional)</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Any modifications, condition notes, recent service, etc." className={textareaClasses} />
            </FormControl>
            <FormDescription>
              Optional information that may affect the valuation.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" size="lg" className="w-full text-base font-semibold" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </> : "Get My Valuation"}
      </Button>
    </Form>;
}